// Stage 5 C-A §8/§9/§10 — Design + Design Issue detail + Feedback.
//
// §8  Design: current formal issue dominant, previous issues as history.
// §9  Design Issue detail: 548-06 — deliverables + exact file versions.
// §10 Provide Feedback: structured Feedback Items with autosave, no
//     generic Save button.  Formal "Submit Consolidated Feedback" or
//     "Approve Issue Without Feedback".  Draft warning on approve.
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { portalApi, fmtDate, daysUntil } from "./portalApi";
import {
    PageHeader, Card, CardHeader, Pill, Button, EmptyState, Spinner,
    Breadcrumbs, AutosaveIndicator,
} from "./PortalUi";
import { usePageMeta } from "../../components/Layout";

function stateTone(s) {
    const x = (s || "").toLowerCase();
    if (x === "issued" || x === "feedback_open") return "accent";
    if (x === "under_review") return "info";
    if (x === "approved" || x === "closed") return "ok";
    if (x === "superseded" || x === "withdrawn") return "neutral";
    return "neutral";
}

export function PortalDesign() {
    usePageMeta({ title: "Design | Studio G4 Portal" });
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        portalApi.design()
            .then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, []);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Design unavailable" note={err} testid="portal-design-err" /></div>;
    if (!data) return <Spinner testid="portal-design-loading" />;

    const { current_issue, history, revision_progress } = data;
    return (
        <div className="py-8 px-4" data-testid="portal-design">
            <PageHeader kicker="Formal design issues" title="Design" testid="portal-design-header" />
            {revision_progress && (
                <Card className="mb-4" testid="portal-design-revision-progress">
                    <div className="p-4 flex items-center gap-8 text-[12.5px]">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Contracted revisions</div>
                            <div className="text-charcoal mt-1 font-manrope text-[15px]" data-testid="portal-design-rounds-included">{revision_progress.rounds_included}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Used</div>
                            <div className="text-charcoal mt-1 font-manrope text-[15px]" data-testid="portal-design-rounds-used">{revision_progress.rounds_used}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Remaining</div>
                            <div className={`mt-1 font-manrope text-[15px] ${revision_progress.rounds_remaining <= 0 ? "text-[#A8543F]" : "text-charcoal"}`} data-testid="portal-design-rounds-remaining">{revision_progress.rounds_remaining}</div>
                        </div>
                    </div>
                </Card>
            )}
            {!current_issue ? (
                <EmptyState
                    heading="No current design issue"
                    note="When your design team issues a formal design package for your review, it will appear here for feedback."
                    testid="portal-design-empty"
                />
            ) : (
                <Card testid="portal-design-current">
                    <CardHeader kicker="Current issue" title={current_issue.title} right={
                        <Pill tone={stateTone(current_issue.state)}>{current_issue.state}</Pill>
                    } />
                    <div className="p-5">
                        {current_issue.client_facing_note && (
                            <p className="text-[13.5px] text-[#4A4A46] leading-relaxed mb-4">{current_issue.client_facing_note}</p>
                        )}
                        <div className="grid grid-cols-3 gap-4 text-[12px] mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Issued</div>
                                <div className="text-charcoal">{fmtDate(current_issue.issued_at)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Feedback due</div>
                                <div className="text-charcoal">{fmtDate(current_issue.feedback_due_date)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Revision round</div>
                                <div className="text-charcoal">{current_issue.revision_round ?? "—"}</div>
                            </div>
                        </div>
                        <div className="text-[12px] text-[#6B6B67] mb-4">{current_issue.deliverable_count || 0} deliverable{(current_issue.deliverable_count || 0) === 1 ? "" : "s"}</div>
                        <div className="flex items-center gap-3">
                            <Link to={`/portal/design/issues/${current_issue.id}`}>
                                <Button variant="primary" testid="portal-design-open-current">Provide feedback →</Button>
                            </Link>
                        </div>
                    </div>
                </Card>
            )}

            {history && history.length > 0 && (
                <div className="mt-6">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A84] font-manrope font-semibold mb-2">Previous issues</div>
                    <Card testid="portal-design-history">
                        <div className="divide-y divide-[#EEEBE4]">
                            {history.map(h => (
                                <div key={h.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`portal-design-history-${h.id}`}>
                                    <div className="min-w-0">
                                        <Link to={`/portal/design/issues/${h.id}`} className="font-manrope text-[13.5px] text-charcoal hover:text-[#A8543F]">{h.title}</Link>
                                        <div className="text-[11.5px] text-[#8A8A84] mt-0.5">Issued {fmtDate(h.issued_at)}</div>
                                    </div>
                                    <Pill tone={stateTone(h.state)}>{h.state}</Pill>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

// ---------- Design Issue detail with structured feedback (§9/§10) ----------

export function PortalDesignIssue() {
    const { issueId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [items, setItems] = useState([]);
    const [saveState, setSaveState] = useState("idle");
    const [savedAt, setSavedAt] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmApprove, setConfirmApprove] = useState(false);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [submitted, setSubmitted] = useState(null);
    const navigate = useNavigate();
    const saveTimer = useRef(null);
    const bootRef = useRef(false);
    const issueLabel = data?.issue?.reference || data?.issue?.title || "Design issue";
    usePageMeta({ title: `${issueLabel} · Design | Studio G4 Portal` });

    useEffect(() => {
        (async () => {
            try {
                const r = await portalApi.designIssue(issueId);
                setData(r.data);
                const d = await portalApi.feedbackDraft(issueId).catch(() => ({ data: {} }));
                if (d.data?.draft?.items?.length) {
                    setItems(d.data.draft.items);
                }
                if (d.data?.draft?.state === "submitted") {
                    setSubmitted({ at: d.data.draft.submitted_at, id: d.data.draft.submission_id });
                }
                bootRef.current = true;
            } catch (e) {
                setErr(e?.response?.data?.detail || "Unavailable");
            }
        })();
    }, [issueId]);

    // Autosave — debounced on items change (no generic Save button).
    useEffect(() => {
        if (!bootRef.current || submitted) return;
        setSaveState("saving");
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                await portalApi.feedbackAutosave(issueId, items, false);
                setSaveState("saved");
                setSavedAt(Date.now());
            } catch {
                setSaveState("error");
            }
        }, 700);
        return () => saveTimer.current && clearTimeout(saveTimer.current);
    }, [items, issueId, submitted]);

    function updateItem(idx, patch) {
        setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
    }
    function addItem() {
        setItems(prev => [...prev, {
            drawing_category: "", drawing_reference: "",
            subject: "", comment: "",
        }]);
    }
    function removeItem(idx) {
        setItems(prev => prev.filter((_, i) => i !== idx));
    }

    async function submitFormal() {
        setSubmitting(true);
        try {
            const r = await portalApi.feedbackSubmit(issueId, items, false);
            setSubmitted({ at: r.data.submitted_at, id: r.data.submission_id, status: r.data.status, message: r.data.message });
            setConfirmSubmit(false);
        } catch (e) {
            alert(e?.response?.data?.detail || "Submission failed");
        } finally { setSubmitting(false); }
    }
    async function approveWithout() {
        setSubmitting(true);
        try {
            const r = await portalApi.feedbackSubmit(issueId, [], true);
            setSubmitted({ at: r.data.submitted_at, id: r.data.submission_id, status: r.data.status, message: r.data.message });
            setConfirmApprove(false);
        } catch (e) {
            alert(e?.response?.data?.detail || "Approval failed");
        } finally { setSubmitting(false); }
    }

    if (err) return <div className="py-8 px-4"><EmptyState heading="Issue unavailable" note={err} testid="portal-design-issue-err" /></div>;
    if (!data) return <Spinner testid="portal-design-issue-loading" />;

    const issue = data.issue;
    const deliverables = data.deliverables || [];
    const validRefs = deliverables.map(d => d.file_version_id);
    const isReadOnly = !!submitted;

    return (
        <div className="py-8 px-4" data-testid="portal-design-issue">
            <Breadcrumbs items={[{ label: "Design", to: "/portal/design" }, { label: issue.title }]} />
            <PageHeader kicker={`Issue · ${fmtDate(issue.issued_at)}`} title={issue.title}
                right={<Pill tone={stateTone(issue.state)}>{issue.state}</Pill>}
                testid="portal-design-issue-header" />

            {submitted && (
                submitted.status === "recorded_pending_processing" ? (
                <div className="mb-6 border border-[#E9D084] bg-[#FFF9E6] rounded-md px-4 py-3 text-[13px] text-[#5C4A16] font-manrope"
                     data-testid="portal-design-issue-submitted-pending-banner">
                    <div className="font-semibold mb-1">Received — being finalised</div>
                    {submitted.message || "Your feedback has been received. We're finalising a few things at our end — please do not submit it again."}
                </div>
                ) : (
                <div className="mb-6 border border-[#C7DFC7] bg-[#EEF6EE] rounded-md px-4 py-3 text-[13px] text-[#2E5A2E] font-manrope"
                     data-testid="portal-design-issue-submitted-banner">
                    {submitted.message || "Your feedback has been received."} Submitted on {fmtDate(submitted.at)}.
                </div>
                )
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card><div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Feedback due</div>
                    <div className="text-[15px] font-manrope font-semibold text-charcoal mt-1">{fmtDate(issue.feedback_due_date)}</div>
                </div></Card>
                <Card><div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Revision round</div>
                    <div className="text-[15px] font-manrope font-semibold text-charcoal mt-1">{issue.revision_round ?? "—"}</div>
                </div></Card>
                <Card><div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Deliverables</div>
                    <div className="text-[15px] font-manrope font-semibold text-charcoal mt-1">{deliverables.length}</div>
                </div></Card>
            </div>

            {issue.client_facing_note && (
                <Card className="mb-6"><div className="p-4 text-[13.5px] text-[#4A4A46] leading-relaxed">{issue.client_facing_note}</div></Card>
            )}

            <Card className="mb-6" testid="portal-design-issue-deliverables">
                <CardHeader title="Deliverables" subtle />
                {deliverables.length === 0 ? (
                    <div className="p-5 text-[13px] text-[#6B6B67]">No deliverables in this issue.</div>
                ) : (
                    <div className="divide-y divide-[#EEEBE4]">
                        {deliverables.map(d => (
                            <div key={d.file_version_id} className="px-5 py-3 flex items-center justify-between gap-3"
                                 data-testid={`portal-issue-deliv-${d.file_version_id}`}>
                                <div className="min-w-0">
                                    <div className="font-manrope text-[14px] text-charcoal truncate">{d.filename || d.file_id}</div>
                                    <div className="text-[11.5px] text-[#8A8A84] mt-0.5 uppercase tracking-[0.06em]">
                                        Version {d.version_number} · {d.content_type || "—"}
                                    </div>
                                </div>
                                <Link to={`/portal/files/${d.file_id}/preview?version_id=${d.file_version_id}`}>
                                    <Button variant="secondary" testid={`portal-issue-view-${d.file_version_id}`}>View</Button>
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Card testid="portal-feedback-composer">
                <CardHeader title="Consolidated feedback"
                    right={!isReadOnly && <AutosaveIndicator state={saveState} savedAt={savedAt} testid="portal-feedback-autosave-indicator" />}
                />
                <div className="p-5">
                    {items.length === 0 && (
                        <div className="mb-4 text-[13px] text-[#6B6B67]">No feedback items yet. Add an item for each comment on the current design.</div>
                    )}
                    <ul className="space-y-4">
                        {items.map((it, idx) => (
                            <li key={idx} className="border border-[#EEEBE4] rounded-md p-4"
                                data-testid={`portal-feedback-item-${idx}`}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="text-[11px] uppercase tracking-[0.1em] text-[#8A8A84] font-manrope font-semibold">Drawing reference</span>
                                        <select disabled={isReadOnly} value={it.drawing_reference || ""} onChange={e => updateItem(idx, { drawing_reference: e.target.value })}
                                            data-testid={`portal-feedback-item-${idx}-ref`}
                                            className="mt-1 w-full px-2 py-1.5 border border-[#D7D4CC] rounded text-[13.5px] font-manrope disabled:bg-[#F7F5EF]">
                                            <option value="">— Select drawing —</option>
                                            {deliverables.map(d => (
                                                <option key={d.file_version_id} value={d.file_version_id}>
                                                    {d.filename || d.file_id} (v{d.version_number})
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className="text-[11px] uppercase tracking-[0.1em] text-[#8A8A84] font-manrope font-semibold">Drawing category</span>
                                        <input disabled={isReadOnly} type="text" value={it.drawing_category || ""} onChange={e => updateItem(idx, { drawing_category: e.target.value })}
                                            data-testid={`portal-feedback-item-${idx}-cat`}
                                            className="mt-1 w-full px-2 py-1.5 border border-[#D7D4CC] rounded text-[13.5px] disabled:bg-[#F7F5EF]" placeholder="e.g. Ground floor plan" />
                                    </label>
                                    <label className="block md:col-span-2">
                                        <span className="text-[11px] uppercase tracking-[0.1em] text-[#8A8A84] font-manrope font-semibold">Subject</span>
                                        <input disabled={isReadOnly} type="text" value={it.subject || ""} onChange={e => updateItem(idx, { subject: e.target.value })}
                                            data-testid={`portal-feedback-item-${idx}-subject`}
                                            className="mt-1 w-full px-2 py-1.5 border border-[#D7D4CC] rounded text-[13.5px] disabled:bg-[#F7F5EF]" />
                                    </label>
                                    <label className="block md:col-span-2">
                                        <span className="text-[11px] uppercase tracking-[0.1em] text-[#8A8A84] font-manrope font-semibold">Comment</span>
                                        <textarea disabled={isReadOnly} rows={3} value={it.comment || ""} onChange={e => updateItem(idx, { comment: e.target.value })}
                                            data-testid={`portal-feedback-item-${idx}-comment`}
                                            className="mt-1 w-full px-2 py-1.5 border border-[#D7D4CC] rounded text-[13.5px] disabled:bg-[#F7F5EF]" />
                                    </label>
                                </div>
                                {!isReadOnly && (
                                    <div className="mt-2 text-right">
                                        <button type="button" onClick={() => removeItem(idx)}
                                            data-testid={`portal-feedback-item-${idx}-remove`}
                                            className="text-[11.5px] text-[#8C2E1E] font-manrope hover:underline">Remove</button>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                    {!isReadOnly && (
                        <div className="mt-4 flex items-center gap-3 flex-wrap">
                            <Button variant="secondary" onClick={addItem} testid="portal-feedback-add-item">+ Add feedback item</Button>
                            <div className="ml-auto flex items-center gap-3 flex-wrap">
                                <Button variant="secondary" onClick={() => setConfirmApprove(true)} testid="portal-feedback-approve-btn">Approve without feedback</Button>
                                <Button variant="primary" disabled={items.length === 0} onClick={() => setConfirmSubmit(true)} testid="portal-feedback-submit-btn">Submit consolidated feedback</Button>
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            {confirmSubmit && (
                <ConfirmModal
                    heading="Submit feedback"
                    body={`You are about to submit ${items.length} feedback item${items.length === 1 ? "" : "s"}. Your project team will be notified and this record will be preserved. You'll need to request a follow-up round if you want to add anything after this.`}
                    confirmLabel="Submit feedback"
                    onConfirm={submitFormal}
                    onCancel={() => setConfirmSubmit(false)}
                    busy={submitting}
                    testid="portal-feedback-confirm-submit"
                />
            )}
            {confirmApprove && (
                <ConfirmModal
                    heading="Approve without feedback"
                    body={items.length > 0
                        ? `You have ${items.length} draft feedback item${items.length === 1 ? "" : "s"} that will NOT be submitted if you approve this issue. Continue?`
                        : "You are approving the current design issue without any feedback. Your project team will be notified."}
                    warn={items.length > 0}
                    confirmLabel="Approve issue"
                    onConfirm={approveWithout}
                    onCancel={() => setConfirmApprove(false)}
                    busy={submitting}
                    testid="portal-feedback-confirm-approve"
                />
            )}
        </div>
    );
}

function ConfirmModal({ heading, body, confirmLabel, onConfirm, onCancel, busy, testid, warn }) {
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid={testid}>
            <div className="bg-white rounded-md max-w-md w-full p-6">
                <h2 className="font-manrope text-[17px] font-semibold text-charcoal mb-2">{heading}</h2>
                <p className={`text-[13.5px] ${warn ? "text-[#8C2E1E]" : "text-[#4A4A46]"} leading-relaxed mb-5`}>{body}</p>
                <div className="flex items-center justify-end gap-3">
                    <Button variant="secondary" onClick={onCancel} disabled={busy} testid={`${testid}-cancel`}>Cancel</Button>
                    <Button variant="primary" onClick={onConfirm} disabled={busy} testid={`${testid}-confirm`}>{busy ? "Submitting…" : confirmLabel}</Button>
                </div>
            </div>
        </div>
    );
}
